import React, { useState, useEffect } from 'react';
import validate from 'validate.js';
import { connect } from 'react-redux';
import * as actions from 'redux/actions/optionOrderActions';
import * as actions_event from 'redux/actions/eventActions';
import { Translate } from 'react-redux-i18n';
import InputDate from 'components/InputDate';
import { AccessTime } from 'theme/icon';

const schema = {
    symbol: {
        presence: { allowEmpty: false, message: 'is required' },
        length: { maximum: 300 },
    },
    market: {
        presence: { allowEmpty: false, message: 'is required' },
        length: { maximum: 50 },
    },
    option_kind: {
        presence: { allowEmpty: false, message: 'is required' },
        length: { maximum: 50 },
    },
};

function resolvePrice(price) {
    const n = Number(price);
    if (Number.isFinite(n) && n > 0) {
        return Number(n.toFixed(2));
    }
    return 0.01;
}

function buildInitialValues(props) {
    return {
        quantity: 1,
        open_price: resolvePrice(props.price),
        strike: props.strike_price,
        open_at: new Date().toISOString().split('T')[0],
        expire_at: props.expire_at || new Date().toISOString().split('T')[0],
        id: props.user_id,
        share: false,
        name: props.name || '',
        symbol: props.symbol || '',
        reference: '',
        option_kind: props.option_kind || '',
        market: props.market || '',
    };
}

const option_kind = [
    { value: 'CALL', label: 'Call' },
    { value: 'PUT', label: 'Put' },
];

const option_market = [
    { value: 'SELL', label: 'STO' },
    { value: 'BUY', label: 'BTO' },
];

const option_strategy = [
    { value: 'CoveredCall', label: 'Covered Call' },
    { value: 'CoveredPut', label: 'Covered Put' },
    { value: 'NakedCall', label: 'Naked Call' },
    { value: 'NakedPut', label: 'Naked Put' },
    { value: 'Another', label: 'Another' },
];

const FormDialog = (props) => {
    const [open, setOpen] = useState(false);
    const [dialogState, setDialogState] = useState({
        isValid: false,
        values: buildInitialValues(props),
        touched: {},
        errors: {},
    });

    const handleClickOpen = () => {
        if (!props.symbol) {
            return;
        }
        setOpen(true);
        setDialogState({
            isValid: false,
            values: buildInitialValues(props),
            touched: {},
            errors: {},
        });
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleBackdropClick = (event) => {
        if (event.target === event.currentTarget) {
            handleClose();
        }
    };

    useEffect(() => {
        const errors = validate(dialogState.values, schema);
        setDialogState((prev) => ({
            ...prev,
            isValid: !errors,
            errors: errors || {},
        }));
    }, [dialogState.values]);

    useEffect(() => {
        if (!open) {
            return;
        }

        setDialogState((prev) => {
            if (prev.touched.open_price) {
                return {
                    ...prev,
                    values: {
                        ...prev.values,
                        symbol: props.symbol || prev.values.symbol,
                        name: props.name || prev.values.name,
                        market: props.market || prev.values.market,
                        option_kind: props.option_kind || prev.values.option_kind,
                        strike: props.strike_price ?? prev.values.strike,
                        expire_at: props.expire_at || prev.values.expire_at,
                    },
                };
            }

            return {
                ...prev,
                values: {
                    ...prev.values,
                    symbol: props.symbol || prev.values.symbol,
                    name: props.name || prev.values.name,
                    market: props.market || prev.values.market,
                    option_kind: props.option_kind || prev.values.option_kind,
                    strike: props.strike_price ?? prev.values.strike,
                    expire_at: props.expire_at || prev.values.expire_at,
                    open_price: resolvePrice(props.price),
                },
            };
        });
    }, [
        open,
        props.symbol,
        props.name,
        props.price,
        props.market,
        props.option_kind,
        props.strike_price,
        props.expire_at,
    ]);

    const handleChange = (event) => {
        const { name, value } = event.target || {};
        if (!name) {
            return;
        }

        setDialogState((prev) => ({
            ...prev,
            values: {
                ...prev.values,
                [name]: value,
            },
            touched: {
                ...prev.touched,
                [name]: true,
            },
        }));
    };

    const handleChangeStrategy = (event) => {
        const { value } = event.target;

        setDialogState((prev) => ({
            ...prev,
            values: {
                ...prev.values,
                reference: value,
            },
            touched: {
                ...prev.touched,
                reference: true,
            },
        }));
    };

    const save = (share) => {
        if (!dialogState.values.symbol) {
            return;
        }

        const payload = {
            ...dialogState.values,
            quantity: Number(dialogState.values.quantity) || 1,
            open_price: resolvePrice(dialogState.values.open_price),
            strike: Number(dialogState.values.strike) || 0,
            ...(share ? { share: true } : {}),
        };

        props.newItem(payload, props.token);
        props.suscribeOption(payload);
        setOpen(false);
    };

    const total = (
        (Number(dialogState.values.quantity) || 0)
        * resolvePrice(dialogState.values.open_price)
        * 100
    ).toFixed(2);

    const sideLabel = dialogState.values.market === 'SELL' ? 'STO' : 'BTO';
    const typeLabel = dialogState.values.option_kind === 'PUT' ? 'Put' : 'Call';
    const uid = `${props.market || 'opt'}-${props.option_kind || 'x'}-${props.strike_price || '0'}`;

    return (
        <div className="trade-entry-modal-trigger">
            <button type="button" className="btn btn-primary" onClick={handleClickOpen}>
                {resolvePrice(props.price).toFixed(2)}
                <span className="tooltip">
                    <AccessTime fontSize="small" aria-hidden="true" />
                    <span className="tooltiptext">Price delay 15min</span>
                </span>
            </button>
            {open && (
                <div className="dialog" role="presentation" onClick={handleBackdropClick}>
                    <div
                        className="dialog-content dialog-content--entry-wide"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={`${uid}-title`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h2 className="dialog-title" id={`${uid}-title`}>
                            Option Entry
                        </h2>
                        <p className="dialog-content-text">
                            {sideLabel} {typeLabel} · Strike {Number(dialogState.values.strike).toFixed(2)}
                        </p>

                        <div className="entry-form">
                            <div className="entry-form-grid entry-form-grid--3">
                                <div className="entry-field">
                                    <label htmlFor={`${uid}-symbol`}>Symbol</label>
                                    <input
                                        id={`${uid}-symbol`}
                                        name="symbol"
                                        type="text"
                                        value={(dialogState.values.symbol || '').toUpperCase()}
                                        disabled
                                    />
                                </div>
                                <div className="entry-field">
                                    <label htmlFor={`${uid}-name`}>Company</label>
                                    <input
                                        id={`${uid}-name`}
                                        name="name"
                                        type="text"
                                        value={dialogState.values.name}
                                        disabled
                                    />
                                </div>
                                <div className="entry-field">
                                    <label htmlFor={`${uid}-reference`}>Strategy</label>
                                    <select
                                        id={`${uid}-reference`}
                                        name="reference"
                                        value={dialogState.values.reference}
                                        onChange={handleChangeStrategy}
                                    >
                                        <option value="">Select strategy</option>
                                        {option_strategy.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <hr className="entry-form-divider" />

                            <div className="entry-form-grid entry-form-grid--3">
                                <div className="entry-field">
                                    <label htmlFor={`${uid}-quantity`}>Contracts</label>
                                    <input
                                        id={`${uid}-quantity`}
                                        name="quantity"
                                        type="number"
                                        min="1"
                                        required
                                        value={dialogState.values.quantity}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="entry-field">
                                    <label htmlFor={`${uid}-market`}>Side</label>
                                    <select
                                        id={`${uid}-market`}
                                        name="market"
                                        value={dialogState.values.market}
                                        disabled
                                    >
                                        {option_market.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="entry-field">
                                    <label htmlFor={`${uid}-kind`}>Type</label>
                                    <select
                                        id={`${uid}-kind`}
                                        name="option_kind"
                                        value={dialogState.values.option_kind}
                                        disabled
                                    >
                                        {option_kind.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="entry-field">
                                    <label htmlFor={`${uid}-strike`}>Strike</label>
                                    <input
                                        id={`${uid}-strike`}
                                        name="strike"
                                        type="number"
                                        value={dialogState.values.strike}
                                        disabled
                                    />
                                </div>
                                <div className="entry-field">
                                    <label htmlFor={`${uid}-price`}>Premium / Debit</label>
                                    <input
                                        id={`${uid}-price`}
                                        name="open_price"
                                        type="number"
                                        min="0.01"
                                        step="0.01"
                                        required
                                        value={dialogState.values.open_price}
                                        onChange={handleChange}
                                    />
                                </div>
                                <div className="entry-field">
                                    <label htmlFor={`${uid}-expire`}>Expiration</label>
                                    <InputDate
                                        id={`${uid}-expire`}
                                        name="expire_at"
                                        required
                                        label=""
                                        value={dialogState.values.expire_at}
                                        disabled
                                    />
                                </div>
                            </div>

                            <div className="entry-field">
                                <label htmlFor={`${uid}-open_at`}>Open date</label>
                                <InputDate
                                    id={`${uid}-open_at`}
                                    name="open_at"
                                    required
                                    label=""
                                    value={dialogState.values.open_at}
                                    onChange={handleChange}
                                    min="1900-01-01"
                                    max={new Date().toISOString().split('T')[0]}
                                />
                            </div>

                            <div className="entry-form-total">$ {total} USD (notional)</div>

                            <div className="dialog-actions">
                                <button type="button" className="btn btn-secondary" onClick={handleClose}>
                                    <Translate value="cancel" />
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    disabled={!dialogState.isValid}
                                    onClick={() => save(true)}
                                >
                                    <Translate value="save_share" />
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    disabled={!dialogState.isValid}
                                    onClick={() => save(false)}
                                >
                                    <Translate value="save" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

function mapStateToProps(reducer) {
    return {
        user_id: reducer.session.auth.id,
        token: reducer.session.auth.token,
    };
}

const mapDispatchToProps = (dispatch) => ({
    newItem: (params, token) =>
        dispatch(actions.newItem({ params, token })),
    suscribeOption: (data) =>
        dispatch(actions_event.suscribeOption(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FormDialog);
